"use client";

import { CustomScroll } from "@/components/CustomScroll";
import { RichText } from "@/components/RichText";
import { Button } from "@/components/ui/Button";
import { useMediaContext } from "@/features/media/useMediaContext";
import { useProjectConfigs } from "@/features/project/ProjectContext";
import { useProjectText } from "@/features/project/useProjectText";
import type { Item, POIPageContent } from "@/types";
import {
  imageFitClass,
  itemDisplay,
  itemImageUrl,
  mainImageSize,
} from "../../itemDisplay";
import { hasAnyFieldData } from "../../poiFields";
import { Description } from "./Description";
import { FilesList } from "./FilesList";
import { POIFields } from "./POIFields";
import { POIGalleries } from "./POIGalleries";
import { POITable } from "./POITable";
import { cn } from "@/lib/cn";

/**
 * The POI details inside the panel that replaces the result list — the body, in
 * its own scroll container, since the panel is a fixed-height column.
 */
export function POIContent({ item }: { item: Item }) {
  return (
    <CustomScroll className="flex-1">
      <POIBody item={item} />
    </CustomScroll>
  );
}

/**
 * The POI itself: a header image with the title over it, then the blocks the
 * tenant configured in `poiPageContents`, then its galleries.
 *
 * Separate from `POIContent` because the server-rendered POI page shows exactly
 * this and scrolls the document instead of a panel — putting the scroll
 * container here would give that page a nested scroll area the height of the
 * viewport.
 */
export function POIBody({ item }: { item: Item }) {
  const configs = useProjectConfigs();
  const t = useProjectText();

  const properties = item.properties ?? {};
  const urlValue = configs.poiUrlField
    ? properties[configs.poiUrlField]
    : undefined;
  const url = typeof urlValue === "string" ? urlValue : null;

  return (
    <div className="relative max-w-full bg-white dark:bg-dark-color">
      <POIHeaderImage item={item} />

      <div className="poi-content flex flex-col items-start mx-5 gap-2">
        <POIBlocks item={item} />

        {configs.showPoiURLButton && url && (
          <Button variant="primary" size="lg" className="mb-2" asChild>
            <a href={url} target="_blank" rel="noopener noreferrer">
              {t(configs.poiUrlField)}
            </a>
          </Button>
        )}
      </div>

      <POIGalleries properties={properties} className="mx-5" />
    </div>
  );
}

/** The banner: the POI's main image with its title and subtitle laid over it. */
export function POIHeaderImage({
  item,
  className,
  headerClass,
}: {
  item: Item;
  className?: string;
  headerClass?: string;
}) {
  const configs = useProjectConfigs();
  const media = useMediaContext();

  const { title, subtitle } = itemDisplay(item, configs, media);
  const headerImage = itemImageUrl(
    item,
    configs,
    media,
    mainImageSize(configs.poiMainImageFit),
  );

  return (
    // The gradient is on the wrapper rather than the image so the title stays
    // legible over a photo of any brightness.
    <div
      className={cn(
        "relative mb-6 h-28 w-full",
        "bg-white before:absolute before:inset-0 before:block before:bg-[linear-gradient(0deg,rgba(0,0,0,0.5)_0%,rgba(0,0,0,0)_50%)] before:content-[''] dark:bg-dark-color tablet:h-56",
        className,
      )}
    >
      {headerImage && (
        // Tenant-uploaded, arbitrary dimensions, same-origin.
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={headerImage}
          alt=""
          className={`mx-auto h-full w-full ${imageFitClass(
            configs.poiMainImageFit,
          )}`}
        />
      )}
      <div
        className={cn(
          "absolute bottom-0 left-0 right-0 py-2 pl-5 pr-7",
          headerClass,
        )}
      >
        <h3 className="line-clamp-3 text-xl font-bold text-white" title={title}>
          {title}
        </h3>
        <p className="truncate text-base text-white">{subtitle}</p>
      </div>
    </div>
  );
}

/**
 * The blocks the tenant configured in `poiPageContents`.
 *
 * A small interpreter: each entry names a type and the property it reads, so the
 * same component tree serves every tenant's POI page without any of them sharing
 * a schema.
 */
export function POIBlocks({ item }: { item: Item }) {
  const configs = useProjectConfigs();

  return (configs.poiPageContents ?? []).map((block, index) => (
    <div key={index}>
      <ContentBlock properties={item.properties ?? {}} block={block} />
    </div>
  ));
}

/**
 * One configured block, over a bare property bag.
 *
 * Properties rather than an `Item` because the same interpreter draws a secondary
 * layer's popup, and there the properties come off a vector tile — there is no
 * POI behind them.
 */
export function ContentBlock({
  properties,
  block,
}: {
  properties: Record<string, unknown>;
  block: POIPageContent;
}) {
  const configs = useProjectConfigs();
  const t = useProjectText();

  switch (block.type) {
    case "text":
      return <Description properties={properties} options={block} />;
    case "table":
      return <POITable properties={properties} options={block} />;
    case "filesList":
      return <FilesList properties={properties} options={block} />;
    case "fields": {
      const index = block.fieldsBlockIndex ?? 0;
      // A block whose every field is empty renders nothing at all, title
      // included — otherwise the heading sits above a blank gap.
      if (!hasAnyFieldData(configs.poiPageFields?.[index], properties))
        return null;

      return (
        <div>
          {block.title && <RichText html={t(block.title)} />}
          <POIFields properties={properties} index={index} />
        </div>
      );
    }
    default:
      // An unknown block type still names a property; legacy printed it, which
      // is how a tenant notices a block they configured but we don't render.
      return <>{block.field}</>;
  }
}
